import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import {
  experienceFacets,
  linkUsageSentence,
  retiredReferenceSentence,
} from '@/lib/experience-labels';
import type { ExperienceCard } from '@matter_extractor/shared';
import { TriangleAlert } from 'lucide-react';

/**
 * The two blocks an experience's card adds to the common tile (refonte
 * modulaire §15; maquette `Main.dc.html`).
 *
 * §15 puts the line of facets under the name and the usage, the warnings and
 * the date in the foot. Neither block is a plain sentence the layer could
 * append, so both ride the slots `versioned-card.tsx` carries -
 * `renderExtra` between the identity row and the pastilles, `renderFooter`
 * between the line of facts and the stamp. Everything the four objects share
 * stays in the layer: the name, the chevron, the pastilles, the greyed-out
 * state of a retired card and the link that wraps it all.
 *
 * ## No avatar, no description: the identity slot is NOT used
 *
 * The mockup's card is the name and the chevron, and the README says why: « la
 * carte d'une expérience n'a pas d'avatar : sa tête est le nom et le chevron,
 * et c'est la ligne de facettes qui porte son identité ». So this file passes
 * no `renderIdentity` and the layer draws its own `<h2>` - which is also what
 * keeps a library somebody switches between reading as one register. The short
 * description is on the card of a PROTOCOL and not here: an experience's
 * description is written for the person who READS the choice page of a link
 * (§13), and the facets answer the operator's question faster.
 *
 * ## The usage line is the experience's own, and it says « liens »
 *
 * An experience's referents are the active links of §5.1 (§4, H25), which is
 * not one of the four kinds `versionedUsageCount` knows - so `card.usage` is
 * empty for every experience and the layer's `usageSentence` would word
 * nothing. `linkUsageSentence` words it from `linkCount`, and it answers the
 * empty string on zero, which is the rule of §4 applied to a count the layer
 * cannot see: « un usage nul ne s'affiche pas ».
 */

/** « 4 pages · Entretien fondateur · Claire empathique » (§15). */
export function ExperienceCardFacets({ card }: { card: ExperienceCard }) {
  const facets = experienceFacets(card);
  return (
    <p data-testid={EXPERIENCE_IDS.cardFacets} className="text-gray-700 text-sm">
      {/*
        The middot lives INSIDE the text of one span per part, and the whole
        span fits on one line so the formatter cannot lift it into a `{' '}` of
        its own. `versioned-card.tsx` spells the reason out at its own `Facet`:
        Chrome measures each text node separately and rounds, so a third node
        shifts the tail of the line by a third of a pixel - enough to make a
        capture of this screen differ from the one before it on a line whose
        characters are identical.
      */}
      {facets[0]}
      {facets.slice(1).map((facet) => (
        <span key={facet} className="text-gray-700">
          {' '}
          · {facet}
        </span>
      ))}
    </p>
  );
}

/**
 * The foot: the usage in links, then one warning LINE per retired reference
 * (§4, §15).
 *
 * A line and not an encart (mockup README): « un encart dans une carte pèserait
 * plus que la carte ». The triangle is `aria-hidden` and the sentence carries
 * the fact, exactly as the protocol's warning line does.
 */
export function ExperienceCardFooter({ card }: { card: ExperienceCard }) {
  const usage = linkUsageSentence(card.linkCount);
  if (usage === '' && card.retiredReferences.length === 0) return null;
  return (
    <div className="space-y-1">
      {usage === '' ? null : (
        <p data-testid={EXPERIENCE_IDS.cardUsage} className="text-gray-700 text-sm">
          {usage}
        </p>
      )}
      {card.retiredReferences.map((entry) => (
        <p
          key={`${entry.kind}-${entry.id}`}
          data-testid={EXPERIENCE_IDS.cardWarning}
          className="flex items-start gap-1.5 text-jaune-deep text-xs leading-4"
        >
          <TriangleAlert aria-hidden="true" className="mt-px size-3.5 shrink-0" />
          <span>{retiredReferenceSentence(entry)}</span>
        </p>
      ))}
    </div>
  );
}
